






import Vue from 'vue'
import Axios from 'axios'
import NoTeamMessage from '@/components/NoTeamMessage'
import fb from '@/firebase'
import helper from '@/helpers'
import abilitiesByRole, { AbilityRole, TeamRole, Ability } from '@/abilitiesByRole'
import ability from '@/ability'
import { UserProfile } from '@/types/users'
import { UnsubscribeFunction } from '../../store/types'
import { isMobile } from 'mobile-device-detect'
import SubscribeCastTemplates from '@/mixins/SubscribeCastTemplates'
import SubscribeAdminProperties from '@/mixins/SubscribeAdminProperties'
import SubscribeTeamRoles from '@/mixins/SubscribeTeamRoles'
import { KeyDict } from '@/types'

export default Vue.extend({
  name: 'AuthManager',
  mixins: [ SubscribeCastTemplates, SubscribeAdminProperties, SubscribeTeamRoles ],
  computed: {
    teamRoles (): KeyDict<TeamRole> {
      return this.$store.direct.state.abilities.teamRoles
    },
    currentUserId (): string {
      return this.$store.direct.state.user.id
    },
    userLoggedIn (): boolean {
      return this.$store.direct.state.user.userLoggedIn
    },
    userProfile (): UserProfile | null {
      return this.$store.direct.state.user.profile
    },
    cefEvent (): boolean {
      return this.$store.direct.state.events.cefEvent
    },
    activeTeam (): string {
      return this.$store.direct.state.team.activeTeam
    },
    currentTeam (): UserProfile[] {
      return this.$store.direct.state.team.currentTeam
    },
    teamRole (): string {
      return this.$store.direct.state.user.teamRole
    },
    teamCheck (): boolean | null {
      return this.$store.direct.state.user.teamCheck
    },
    teamLoaded (): boolean {
      return this.$store.direct.state.team.teamLoaded
    },
    userHasNoTeams (): boolean {
      return this.$store.direct.state.user.userHasNoTeams
    },
    showTeamMessage (): boolean {
      let status = false
      if (!this.cefEvent) {
        if (this.teamCheck === false && this.userLoggedIn) {
          status = true
        }
      }
      return status
    },
    loading (): boolean {
      return this.userLoggedIn && !this.teamLoaded && this.teamCheck === null
    },
    loaded (): boolean {
      return !!(this.userLoggedIn && this.teamLoaded && this.teamCheck)
    },
    defaultPageOnLogin (): string|null {
      return this.$store.direct.state.user.defaultPageOnLogin
    }
  },
  watch: {
    defaultPageOnLogin (newVal: string|null) {
      if ((['login', 'invite', 'home', 'events'].includes(this.$route.name ?? '')) && newVal !== null && newVal !== this.$route.name) {
        const url = new URL(window.location.href)
        const redirect = url.searchParams.get('redirect')
        if (redirect) {
          this.$router.push(redirect)
        } else {
          this.$router.push({ name: newVal })
        }
      }
    },
    activeTeam (newVal: string) {
      // if we switch teams get the new team members
      if (newVal !== '') {
        this.$store.direct.dispatch.team.unsubscribeCurrentTeams()
        this.$store.direct.dispatch.team.subscribeCurrentTeam(newVal)
        this.$store.direct.dispatch.team.subscribeTeamStreams(newVal)
        this.$store.direct.dispatch.team.getTeamCastProperties()
        this.$store.direct.dispatch.team.subscribeTeamInvites(newVal)
      }
    },
    userLoggedIn (newVal: boolean) {
      if (newVal) {
        this.getUserProfile()
      } else {
        this.unsubscribe()
        this.$store.direct.dispatch.team.resetTeam()
        ability.update([])
      }
    },
    userProfile (newVal: UserProfile | null, oldVal: UserProfile | null) {
      if (this.agentUpdated) return
      if (newVal === null) return
      const agent = helper.getAgent()
      // @ts-ignore
      agent.ipaddress = this.clientIpAddress
      if (newVal.agent === undefined || !helper.compareDicts(newVal.agent, agent)) {
        this.$store.direct.dispatch.user.updateProfile({ id: this.currentUserId, agent })
        this.agentUpdated = true
      }
      if (newVal !== null && oldVal !== null && newVal.role !== oldVal.role) {
        this.updateAbilities()
      }
    },
    teamLoaded (newVal: boolean) {
      if (newVal === true && this.userProfile?.role !== 'superuser') {
        const userInTeam = this.currentTeam.find(user => user.user_id === this.currentUserId)
        if (userInTeam) {
          this.$store.direct.dispatch.user.setUserRoleOnTeam(userInTeam.role)
          // TeamRole can now also be a teams_roles ID
          // needs to wait till team and rollen
          this.updateAbilities()
        }
      }
    },
    loaded (newVal: boolean, oldVal: boolean) {
      if (newVal === true && oldVal === false && !this.cefEvent) {
        this.$store.direct.dispatch.user.completeLogin()
      }
    },
    teamRoles: {
      handler (_newRoles: KeyDict<TeamRole>) {
        this.updateAbilities()
      },
      deep: true
    },
    teamRole () {
      this.updateAbilities()
    }
  },
  async created () {
    await this.fetchClientIpAddress()
    if (this.userLoggedIn === true) {
      this.getUserProfile()
    }
    // check to see if this is the cef version of the page on load
    if (window.location.href.includes('cef') || window.location.href.includes('tweet')) {
      this.$store.direct.dispatch.events.setCefEvent()
    }
    //
    if (!this.cefEvent) {
      fb.firebase.auth().onIdTokenChanged(user => {
        if (user === null) {
          if (this.userLoggedIn === true) {
            this.$store.direct.dispatch.user.resetUser()
          }
        } else if (this.userLoggedIn === false) {
          this.$store.direct.dispatch.user.signInUser(user).catch(error => {
            console.error('Sign in user Error', error)
          })
        }
      })
    } else {
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      const cefMode = urlParams.get('cef-mode')
      const cefMixer = urlParams.get('mixer-id')
      if (cefMode !== '' && cefMode !== null) {
        this.$store.direct.dispatch.events.setCefMode(cefMode)
      }
      if (cefMixer !== '' && cefMixer !== null) {
        this.$store.direct.dispatch.events.setCefMixer(cefMixer)
      }
      if (token) {
        const refreshToken = fb.functions.httpsCallable('refreshToken')
        refreshToken({ token: token }).then(result => {
          fb.firebase.auth().signInWithCustomToken(result.data.token).then(res => {
            this.$store.direct.dispatch.user.signInUser(res.user).then(() => {
              this.getUserProfile()
            }).catch(error => {
              console.error('Could not refresh token', error)
            })
          }).catch(error => {
            console.error('Could not refresh token', error)
          })
        }).catch(error => {
          console.error('Could not refresh token', error)
        })
      }
    }
  },
  beforeDestroy () {
    this.unsubscribe()
    this.$store.direct.dispatch.team.unsubscribeTeamStreams()
    this.$store.direct.dispatch.team.unsubscribeCurrentTeams()
    this.$store.direct.dispatch.user.unsubscribeOnlineStatus()
  },
  data: () => ({
    unsubscribeFunction: null as null|UnsubscribeFunction,
    agentUpdated: false,
    clientIpAddress: ''
  }),
  methods: {
    unsubscribe () {
      if (this.unsubscribeFunction !== null) {
        this.unsubscribeFunction()
        this.unsubscribeFunction = null
      }
    },
    getUserAbilityRole (role: string): string|undefined {
      for (const ability in AbilityRole) {
        if (role === AbilityRole[ability]) {
          return AbilityRole[ability]
        }
      }
      const teamrole: TeamRole|undefined = this.teamRoles[role]
      return teamrole?.name
    },
    getUserProfile () {
      this.unsubscribe()
      this.unsubscribeFunction = fb.db.collection('profiles').doc(this.currentUserId).onSnapshot((profile) => {
        const profileData = profile.data() as UserProfile
        if (profile.exists === true && profileData) {
          this.$store.direct.dispatch.user.setUserProfile(profileData)
          this.$store.direct.dispatch.user.subscribeOnlineStatus()
          if (this.userLoggedIn && profileData.role === AbilityRole.SuperUser && !isMobile) {
            ability.update(abilitiesByRole(AbilityRole.SuperUser))
          }
        }
      }, (error) => {
        // we cannot log them in so give them an error
        if (this.userProfile === null) {
          this.$store.direct.dispatch.user.logoutUser()
          setTimeout(() => {
            this.$store.direct.commit.user.toggleUserHasNoTeams(true)
          }, 200)
        }
        this.$store.direct.dispatch.logs.addLog({
          message: `User ${this.currentUserId} tried to log in, but is no longer on a team`,
          level: 'info'
        })
        console.log(error)
        this.$store.direct.dispatch.logs.addLog({
          message: error.message,
          level: 'info'
        })
      })
    },
    async fetchClientIpAddress () {
      /* Fetch IP info, expected response:
        someProp=someValue
        ip=clientIpAddress
        someOtherProp=someOtherValue
      */

      const response = await Axios.get('https://www.cloudflare.com/cdn-cgi/trace')
      const infoArr = response?.data ? response.data.split('\n') : null
      const ipString = infoArr ? infoArr.find((entry) => entry.startsWith('ip=')) : null
      this.clientIpAddress = ipString ? ipString.split('=')[1] : 'Fetching IP address had failed'
    },
    updateAbilities () {
      let abilities: Ability[]|undefined = undefined
      const teamRole = this.teamRoles[this.teamRole]
      if (teamRole !== undefined) abilities = teamRole.toAbilities()
      if (Object.values(AbilityRole).includes(this.teamRole as AbilityRole)) {
        abilities = abilitiesByRole(this.teamRole as AbilityRole)
      }
      if (isMobile) abilities = abilitiesByRole(AbilityRole.GuestCaster)
      if (abilities !== undefined) ability.update(abilities)
    }
  },
  components: {
    NoTeamMessage
  }
})
